import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    ElementRef,
    input,
    output,
    signal,
    viewChildren,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { RaButtonComponent, RaInputComponent } from '@shared/components';

@Component({
    selector: 'modal-confirm-code',
    templateUrl: 'modal-confirm-code.component.html',
    styleUrl: 'modal-confirm-code.component.scss',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RaButtonComponent, RaInputComponent, TranslatePipe],
})
export class ModalConfirmCodeComponent {
    // * Inputs
    public emailPlaceholder = input<string>('');
    public resendLoading = input<boolean>(false);
    public confirmLoading = input<boolean>(false);
    public showResendSection = input<boolean>(false);

    // * Outputs
    public confirm = output<string>();
    public resendCode = output<string>();

    public readonly codeLength = 6;
    public readonly digitIndexes = Array.from({ length: this.codeLength }, (_, index) => index);
    public otpDigits = signal<string[]>(Array.from({ length: this.codeLength }, () => ''));
    public emailFormControl = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
    });
    private readonly digitInputRefs = viewChildren<ElementRef<HTMLInputElement>>('digitInput');
    public canConfirm = computed(() => {
        return this.otpDigits().every(digit => digit.length === 1);
    });

    public constructor() {
        effect(() => {
            if (this.resendLoading()) {
                this.emailFormControl.disable({ emitEvent: false });
            } else {
                this.emailFormControl.enable({ emitEvent: false });
            }
        });
    }

    public handleConfirm(): void {
        if (!this.canConfirm()) return;

        const code = this.otpDigits().join('');
        this.confirm.emit(code);
    }

    public handleResend(): void {
        if (!this.canResend() || this.resendLoading()) return;
        this.resendCode.emit(this.emailFormControl.value.trim());
    }

    public canResend(): boolean {
        return this.emailFormControl.valid;
    }

    public handleDigitInput(index: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        const sanitized = this.sanitizeCodeValue(input.value);

        if (!sanitized) {
            this.updateDigit(index, '');
            input.value = '';
            return;
        }

        if (sanitized.length > 1) {
            this.applyDigitSequence(index, sanitized.slice(0, this.codeLength));
            return;
        }

        input.value = sanitized;
        this.updateDigit(index, sanitized);

        if (index < this.codeLength - 1) {
            this.focusDigit(index + 1);
        }
    }

    public handleDigitKeydown(index: number, event: KeyboardEvent): void {
        const input = event.target as HTMLInputElement;

        if (event.key === 'ArrowLeft' && index > 0) {
            event.preventDefault();
            this.focusDigit(index - 1);
            return;
        }

        if (event.key === 'ArrowRight' && index < this.codeLength - 1) {
            event.preventDefault();
            this.focusDigit(index + 1);
            return;
        }

        if (event.key === 'Backspace') {
            if (input.value) {
                this.updateDigit(index, '');
                input.value = '';
                return;
            }

            if (index > 0) {
                event.preventDefault();
                this.updateDigit(index - 1, '');
                this.focusDigit(index - 1);
            }
            return;
        }

        if (event.key === 'Delete') {
            this.updateDigit(index, '');
            input.value = '';
            return;
        }

        if (event.key.length === 1 && !this.isAllowedCharacter(event.key)) {
            event.preventDefault();
        }
    }

    public handleDigitPaste(index: number, event: ClipboardEvent): void {
        event.preventDefault();
        const pasted = this.sanitizeCodeValue(event.clipboardData?.getData('text') ?? '');

        if (!pasted) {
            return;
        }

        this.applyDigitSequence(index, pasted.slice(0, this.codeLength));
    }

    private applyDigitSequence(startIndex: number, digits: string): void {
        const next = [...this.otpDigits()];

        for (let i = startIndex; i < this.codeLength; i++) {
            next[i] = digits[i - startIndex] ?? next[i];
        }

        this.otpDigits.set(next);
        this.focusDigit(Math.min(startIndex + digits.length, this.codeLength - 1));
    }

    private updateDigit(index: number, value: string): void {
        const next = [...this.otpDigits()];
        next[index] = value;
        this.otpDigits.set(next);
    }

    private focusDigit(index: number): void {
        setTimeout(() => {
            const input = this.digitInputRefs()[index]?.nativeElement;
            input?.focus();
            input?.select();
        }, 0);
    }

    private sanitizeCodeValue(value: string): string {
        return value.replace(/[^a-zA-Z0-9]/g, '');
    }

    private isAllowedCharacter(value: string): boolean {
        return /^[a-zA-Z0-9]$/.test(value);
    }
}
